import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { setB2BToken, getB2BToken, clearB2BImpersonator } from './auth';

/* B2B ("entrepreneur") sign-in. Self-contained — no imports from the
 * intranet's routes/, app/, or features/ — so the whole `b2b/` tree can be
 * lifted into a standalone app later. Raw fetch, no Redux/RTK Query.
 *
 * Two-step flow (step 2 branches, mirroring the client portal):
 *   1. email + password.
 *   2a. First login / post-reset (server responds needs_password_change): the
 *       user chooses a new password. No emailed code — the temporary password
 *       they just used came by email, so email possession is already proven.
 *   2b. Established user (server responds needs_verification): the user enters
 *       the 6-digit 2FA code we emailed. The server returns a session token
 *       only after step 2 is cleared.
 *
 * Plus "Forgot your password?" (same design as the client portal):
 *   'forgot' → request an emailed reset link.
 *   'reset'  → arrived from that link (#reset=<token>): choose a new password.
 *              This does not sign the user in - they sign in with it next and
 *              still get the emailed code.
 */

const c = {
  navy:   '#0a2947',
  navyDk: '#06192c',
  teal:   '#0069a6',
  gold:   '#d09b2c',
  ink:    '#1d2733',
  subtle: '#58585b',
  line:   '#d8d5cf',
  canvas: '#f4f2ee',
  surface:'#ffffff',
  error:  '#9a1c1c',
};

const MIN_PASSWORD_LENGTH = 8;

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth <= 900 : false);
  useEffect(() => {
    const onResize = () => setM(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return m;
}

export default function B2BLogin() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [phase, setPhase] = useState('login'); // 'login' | 'verify' | 'setpw' | 'forgot' | 'reset'
  // From the emailed link's #reset=<token>. A fragment rather than a query
  // string on purpose: fragments are never sent to a server, so the token
  // stays out of access logs and Referer headers.
  const [resetToken, setResetToken] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Arrived from a reset link? Show the new-password form. Otherwise, if
  // already signed in, skip straight to the dashboard.
  useEffect(() => {
    const match = window.location.hash.match(/^#reset=(.+)$/);
    if (match) {
      setResetToken(match[1]);
      setPhase('reset');
      // Drop the token from the address bar and history once read.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return;
    }
    if (getB2BToken()) navigate('/entrepreneur/dashboard', { replace: true });
  }, [navigate]);

  const backToLogin = () => {
    setPhase('login'); setError(null); setNotice(null); setResetToken(null);
    setPassword(''); setCode(''); setNewPassword(''); setConfirmPassword('');
  };

  const toForgot = () => { setPhase('forgot'); setError(null); setNotice(null); };

  const finish = (token) => {
    // A real sign-in is never an impersonation — clear any stale marker left in
    // this browser from a prior admin "log in as" session.
    clearB2BImpersonator();
    setB2BToken(token);
    navigate('/entrepreneur/dashboard', { replace: true });
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setError(null); setNotice(null);
    const cleaned = email.trim();
    if (!cleaned || !password) { setError('Please enter your email and password.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch('/api/b2b/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Invalid email or password.');
      if (data.needs_password_change) {
        setEmail(data.email || cleaned);
        setPhase('setpw');
        setNotice('First time signing in — choose a password you’ll use from now on.');
      } else if (data.needs_verification) {
        setEmail(data.email || cleaned);
        setPhase('verify');
        setNotice('We emailed you a 6-digit verification code. Enter it below to finish signing in.');
      } else if (data.token) {
        finish(data.token);
      } else {
        throw new Error('Unexpected response. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Sign-in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setError(null);
    if (!code.trim()) { setError('Enter the verification code we emailed you.'); return; }
    setSubmitting(true);
    try {
      // Re-send the password from phase 1 alongside the code — the server
      // re-verifies it so a leaked code alone can't sign in.
      const res = await fetch('/api/b2b/verify_login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password, code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Invalid or expired verification code.');
      finish(data.token);
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitSetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Your new password must be at least ${MIN_PASSWORD_LENGTH} characters.`); return;
    }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      // Re-send the temporary password from phase 1 as proof of identity, along
      // with the chosen password — the server re-verifies it (no emailed code).
      const res = await fetch('/api/b2b/set_initial_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not set your password. Please try again.');
      finish(data.token);
    } catch (err) {
      setError(err.message || 'Could not set your password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // "Forgot your password?" - always answers the same way, whether or not the
  // address has an account (the backend makes the same promise).
  const submitForgot = async (e) => {
    e.preventDefault();
    setError(null);
    const cleaned = email.trim();
    if (!cleaned) { setError('Please enter your email address.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch('/api/b2b/forgot_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not send the reset email. Please try again.');
      setPhase('login');
      setPassword('');
      setNotice(`If an account exists for ${cleaned}, we have emailed a link to reset your password. It expires in 60 minutes.`);
    } catch (err) {
      setError(err.message || 'Could not send the reset email. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Your new password must be at least ${MIN_PASSWORD_LENGTH} characters.`); return;
    }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch('/api/b2b/reset_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Could not reset your password. Please try again.');
      backToLogin();
      setNotice('Your password has been updated. Please sign in with your new password.');
    } catch (err) {
      setError(err.message || 'Could not reset your password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: c.canvas, color: c.ink, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      {/* Full-width goldenrod bar across the very top of the page. */}
      <div style={{ height: 3, background: c.gold, flexShrink: 0 }} />

      {/* Split: form column / brand panel */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', minHeight: 0 }}>
      {/* Form column */}
      <div style={{ background: c.surface, padding: isMobile ? '2rem 1.5rem' : '3rem 3.5rem', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Brand mark — pinned to the top of the column */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '2rem' }}>
          <div aria-hidden="true" style={{ width: 30, height: 30, background: c.gold, WebkitMask: 'url(/pca.png) center / contain no-repeat', mask: 'url(/pca.png) center / contain no-repeat' }} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
            <span style={{ color: c.navy, fontWeight: 700, fontSize: '0.85rem' }}>Prosperity Capital Advisors</span>
            <span style={{ color: c.gold, fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 4 }}>Entrepreneur Workspace</span>
          </div>
        </div>

        {/* Heading + form, vertically centered on desktop then lifted via
            paddingBottom so the "Sign in" heading and its subtitle line up
            with the brand panel's "Entrepreneur Workspace" eyebrow on the
            right. (Bottom padding shrinks the centered content box from below,
            shifting it up by half the padding.) The brand mark stays at the
            top and the footer at the bottom. On mobile the brand panel is
            hidden, so we keep the block top-aligned. */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          ...(isMobile ? {} : { flex: 1, justifyContent: 'center', minHeight: 0, paddingBottom: '22px' }),
        }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: c.navy, margin: '0 0 0.4rem' }}>
          {phase === 'login' ? 'Sign in'
            : phase === 'verify' ? 'Verify it’s you'
              : phase === 'forgot' ? 'Reset your password'
                : phase === 'reset' ? 'Choose a new password'
                  : 'Set up your account'}
        </h1>
        <p style={{ color: c.subtle, fontSize: '0.88rem', margin: '0 0 1.5rem', maxWidth: 420 }}>
          {phase === 'login'
            ? 'Access your firm’s workspace with Prosperity Capital Advisors.'
            : phase === 'verify'
              ? 'Enter the verification code we emailed you to finish signing in.'
              : phase === 'forgot'
                ? 'Enter your email address and we’ll send you a link to choose a new password.'
                : 'Choose a password you’ll use going forward.'}
        </p>

        {notice && (
          <div style={{ background: 'rgba(0,105,166,0.08)', border: `1px solid rgba(0,105,166,0.3)`, color: c.teal, borderRadius: 6, padding: '0.6rem 0.85rem', fontSize: '0.85rem', marginBottom: '1rem', maxWidth: 420 }}>
            <i className="bi bi-envelope-check me-2" />{notice}
          </div>
        )}
        {error && (
          <div role="alert" style={{ background: 'rgba(154,28,28,0.06)', border: `1px solid rgba(154,28,28,0.3)`, color: c.error, borderRadius: 6, padding: '0.6rem 0.85rem', fontSize: '0.85rem', marginBottom: '1rem', maxWidth: 420 }}>
            <i className="bi bi-exclamation-circle-fill me-2" />{error}
          </div>
        )}

        {phase === 'login' ? (
          <form onSubmit={submitLogin} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 420 }}>
            <Field label="Email address">
              <input type="email" autoComplete="username" autoFocus value={email} onChange={e => setEmail(e.target.value)} placeholder="you@firm.com" disabled={submitting} style={inputStyle} />
            </Field>
            <Field label="Password">
              <input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••••••" disabled={submitting} style={inputStyle} />
            </Field>
            <SubmitButton submitting={submitting} label="Sign in" />
            <button type="button" onClick={toForgot} disabled={submitting} style={linkButtonStyle}>
              Forgot your password?
            </button>
          </form>
        ) : phase === 'forgot' ? (
          <form onSubmit={submitForgot} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 420 }}>
            <Field label="Email address">
              <input type="email" autoComplete="username" autoFocus value={email} onChange={e => setEmail(e.target.value)} placeholder="you@firm.com" disabled={submitting} style={inputStyle} />
            </Field>
            <SubmitButton submitting={submitting} label="Email me a reset link" />
            <button type="button" onClick={backToLogin} style={linkButtonStyle}>
              ← Back to sign in
            </button>
          </form>
        ) : phase === 'verify' ? (
          <form onSubmit={submitVerify} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 420 }}>
            <Field label="Verification code">
              <input inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={e => setCode(e.target.value)} placeholder="6-digit code" disabled={submitting} style={{ ...inputStyle, letterSpacing: '0.3em', fontSize: '1rem' }} />
            </Field>
            <SubmitButton submitting={submitting} label="Verify & continue" />
            <button type="button" onClick={backToLogin} style={linkButtonStyle}>
              ← Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={phase === 'reset' ? submitReset : submitSetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', maxWidth: 420 }}>
            {/* Hidden username field so browsers/password managers anchor the
                new password to the account, instead of treating the first
                password field as the username (which unmasks it and prompts
                "save this username and password"). Deliberately NOT display:none
                — browsers ignore hidden fields for autofill association. */}
            <input type="text" name="username" autoComplete="username" value={email} readOnly
                   tabIndex={-1} aria-hidden="true"
                   style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
            <Field label="New password">
              <input type="password" autoComplete="new-password" autoFocus value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`} disabled={submitting} style={inputStyle} />
            </Field>
            <Field label="Confirm new password">
              <input type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" disabled={submitting} style={inputStyle} />
            </Field>
            <SubmitButton submitting={submitting} label={phase === 'reset' ? 'Save new password' : 'Set password & continue'} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={backToLogin} style={linkButtonStyle}>
                ← Back to sign in
              </button>
              {phase === 'reset' && error && (
                <button type="button" onClick={toForgot} disabled={submitting} style={linkButtonStyle}>
                  Request a new link
                </button>
              )}
            </div>
          </form>
        )}
        </div>

        {/* Footer — pinned to the bottom of the column */}
        <div style={{ marginTop: 'auto', paddingTop: '2rem', color: c.subtle, fontSize: '0.7rem' }}>
          © {new Date().getFullYear()} Prosperity Capital Advisors · Registered Investment Adviser
        </div>
      </div>

      {/* Brand panel */}
      {!isMobile && (
        <div style={{ position: 'relative', overflow: 'hidden', background: c.navy, color: 'white', padding: '3rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ color: c.gold, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.24em', fontWeight: 600, marginBottom: '1rem' }}>
            Entrepreneur Workspace
          </div>
          <h2 style={{ fontSize: '1.7rem', lineHeight: 1.3, fontWeight: 700, margin: 0, maxWidth: 460 }}>
            The tools and team of Prosperity Capital Advisors — <span style={{ color: c.gold }}>working alongside your firm.</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', lineHeight: 1.6, marginTop: '1.1rem', maxWidth: 440 }}>
            Sign in to your firm’s dedicated workspace to collaborate with our staff and access shared tools.
          </p>
        </div>
      )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6,
  padding: '0.65rem 0.85rem', fontSize: '0.9rem', color: c.ink, outline: 'none',
};

const linkButtonStyle = {
  background: 'transparent', border: 'none', padding: 0, color: c.teal,
  fontSize: '0.8rem', cursor: 'pointer', alignSelf: 'flex-start',
};

function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', color: c.subtle, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>{label}</span>
      {children}
    </label>
  );
}

function SubmitButton({ submitting, label }) {
  return (
    <button type="submit" disabled={submitting} style={{
      marginTop: '0.3rem', background: c.navy, color: 'white', border: 0, borderRadius: 6,
      padding: '0.75rem 1.25rem', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.04em',
      cursor: submitting ? 'wait' : 'pointer', textTransform: 'uppercase',
    }}>
      {submitting ? 'Please wait…' : label}
    </button>
  );
}

Field.propTypes = { label: PropTypes.string, children: PropTypes.node };
SubmitButton.propTypes = { submitting: PropTypes.bool, label: PropTypes.string };
